import { observable, computed, action } from 'mobx'
import { has, last } from 'lodash'

import BaseListStore, { IBaseListAddOptions } from 'stores/base/BaseListStore'
import BaseListItem, {
  IBaseListItemUpdateOptions,
} from 'stores/base/BaseListItem'
import FieldStore from 'stores/field/FieldStore'
import Field from 'stores/field/Field'
import ModelStore from 'stores/model/ModelStore'
import Model from 'stores/model/Model'
import ScriptStore from 'stores/script/ScriptStore'
import Script from 'stores/script/Script'
import DMMFStore from 'stores/dmmf/DMMFStore'

import createError from 'utils/createError'
import isWhereFilterValid from 'utils/isWhereFilterValid'

export interface IScriptWhereCreatePayload {
  id: string
  fieldIds: string[]
  operation?: string
  value: string | null
  scriptId: string
  enabled?: boolean
}

export type IScriptWhereUpdatePayload = Partial<IScriptWhereCreatePayload> & {
  id: string
}

// Where Item
export class ScriptWhereItem extends BaseListItem {
  @observable id: IScriptWhereCreatePayload['id']
  @observable fieldIds: IScriptWhereCreatePayload['fieldIds']
  @observable operation: string
  @observable value: IScriptWhereCreatePayload['value']
  @observable scriptId: string
  @observable enabled: boolean

  constructor(where: IScriptWhereCreatePayload) {
    super(where)

    this.id = where.id
    this.fieldIds = where.fieldIds
    this.operation = where.operation || this.supportedOperations[0]
    this.value = where.value
    this.scriptId = where.scriptId
    this.enabled = where.enabled || true
  }

  @computed
  get fields(): Field[] {
    return this.fieldIds.map(fId => {
      const field = FieldStore.get(fId)
      if (!field) {
        throw createError('ScriptWhereItem.fields', 'Invalid fieldId', {
          fieldId: fId,
        })
      }
      return field
    })
  }

  @computed
  get script(): Script {
    const script = ScriptStore.get(this.scriptId)

    if (!script) {
      throw createError('ScriptWhereItem.script', 'Invalid scriptId', {
        scriptId: this.scriptId,
      })
    }
    return script
  }

  @computed
  get supportedOperations(): string[] {
    // Find the inputType from DMMFStore with a specific name, and look at its fields to figure out what operations are possible

    const field = last(this.fields)
    if (!field) return []
    let inputTypeName: string
    if (field.isRequired && field.isString) {
      inputTypeName = 'StringFilter'
    } else if (!field.isRequired && field.isString) {
      inputTypeName = 'StringNullableFilter'
    } else if (field.isRequired && field.isInt) {
      inputTypeName = 'IntFilter'
    } else if (!field.isRequired && field.isInt) {
      inputTypeName = 'IntNullableFilter'
    } else if (field.isRequired && field.isFloat) {
      inputTypeName = 'FloatFilter'
    } else if (!field.isRequired && field.isFloat) {
      inputTypeName = 'FloatNullableFilter'
    } else if (field.isRequired && field.isBigInt) {
      inputTypeName = 'BigIntFilter'
    } else if (!field.isRequired && field.isBigInt) {
      inputTypeName = 'BigIntNullableFilter'
    } else if (field.isRequired && field.isDecimal) {
      inputTypeName = 'DecimalFilter'
    } else if (!field.isRequired && field.isDecimal) {
      inputTypeName = 'DecimalNullableFilter'
    } else if (field.isRequired && field.isBoolean) {
      inputTypeName = 'BoolFilter'
    } else if (!field.isRequired && field.isBoolean) {
      inputTypeName = 'BoolNullableFilter'
    } else if (field.isRequired && field.isDateTime) {
      inputTypeName = 'DateTimeFilter'
    } else if (!field.isRequired && field.isDateTime) {
      inputTypeName = 'DateTimeNullableFilter'
    } else if (field.isRequired && field.isJson) {
      inputTypeName = 'JsonFilter'
    } else if (!field.isRequired && field.isJson) {
      inputTypeName = 'JsonNullableFilter'
    } else if (field.isRequired && field.isBytes) {
      inputTypeName = 'BytesFilter'
    } else if (!field.isRequired && field.isBytes) {
      inputTypeName = 'BytesNullableFilter'
    } else if (field.isRequired && field.isEnum) {
      inputTypeName = `Enum${field.type}Filter`
    } else if (!field.isRequired && field.isEnum) {
      inputTypeName = `Enum${field.type}NullableFilter`
    } else if (field.isRelation) {
      inputTypeName = `${field.type}WhereInput`
    } else {
      throw createError(
        'ScriptWhere.supportedOperations',
        'Unsupported field for `where` filter',
        {
          fields: this.fields.map(f => f?.serialize()),
        }
      )
    }

    const inputType = DMMFStore.inputObjectTypes.get(inputTypeName)
    if (!inputType) {
      throw createError(
        'ScriptWhere.supportedOperations',
        'Could not find appropriate InputType for this field type. This should never happen.',
        {
          inputTypeName,
          fields: this.fields.map(f => f?.serialize()),
        }
      )
    }

    const operations = inputType.fields
      .map(f => f.name)
      .filter(f => f !== 'mode') // filter out `mode`

    if (!field.isRequired && !field.isRelation) {
      return operations.concat(['isNull', 'isNotNull'])
    }

    return operations
  }

  @computed
  get isValid(): boolean {
    return isWhereFilterValid(this)
  }

  /**
   * Given an index into `this.fieldIds`, returns a list of fields at that level that filters can be applied on
   */
  getFilterableFieldsAtIndex(idx: number): Field[] {
    if (idx >= this.fieldIds.length) {
      throw createError(
        'ScriptWhere.getFilterableFieldsAtIndex',
        'Index is out of range',
        {
          fieldIds: this.fieldIds,
          index: idx,
        }
      )
    }

    if (idx === 0) {
      return this.script.model.fields.filter(
        f =>
          (f.isScalar && !f.isList) || (f.isEnum && !f.isList) || f.isRelation
      )
    }

    if (idx === 1) {
      const field = FieldStore.get(this.fieldIds[idx - 1])
      // The fields that are to be listed on this "level" or "index" depend on what the field's type is
      return field!.typeAsModel!.fields.filter(
        f => (f.isScalar && !f.isList) || (f.isEnum && !f.isList)
      )
    }

    // Artificially limit the number of levels you can filter to to 2
    return []
    // TODO:: Uncomment when we support arbitrary levels of filters
    // const field = FieldStore.get(this.fieldIds[idx - 1])
    // // The fields that are to be listed on this "level" or "index" depend on what the field's type is
    // const model = field?.typeAsModel || this.script.model // Default to the script model to account for the case when you're at index 0, and therefore `field` is undefined
    // return model.fields.filter(f => (f.isScalar && !f.isList) || f.isRelation)
  }

  @action
  update = (
    where: Partial<IScriptWhereCreatePayload>,
    options?: IBaseListItemUpdateOptions
  ) => {
    has(where, 'fieldIds') && (this.fieldIds = where.fieldIds!)
    has(where, 'operation') && (this.operation = where.operation!)
    has(where, 'value') && (this.value = where.value!)
    has(where, 'enabled') && (this.enabled = where.enabled!)

    // This makes sure the Script itself knows that something changed. Will indirectly make sure that changes to where are svaed back to IndexedDB
    this.script.update({ where: [] }) // The actual value does not matter, because that comes from the `serialize` function below

    super.update(where, options)
  }

  serialize = (): IScriptWhereCreatePayload => {
    return {
      id: String(this.id),
      fieldIds: [...this.fieldIds], // Need to create a new object because property fieldIds cannot be saved as a proxy to indexDB
      operation: String(this.operation),
      value: this.value, // Don't Stringify it because it may be `null`
      scriptId: String(this.scriptId),
      enabled: this.enabled,
    }
  }
}

// Where Store
class ScriptWhere extends BaseListStore<
  ScriptWhereItem,
  IScriptWhereCreatePayload
> {
  @observable modelId: string
  @observable scriptId: string

  constructor({ modelId, scriptId }: { modelId: string; scriptId: string }) {
    super(ScriptWhereItem)

    this.modelId = modelId
    this.scriptId = scriptId
  }

  @computed
  get model(): Model {
    let model = ModelStore.get(this.modelId)
    if (!model) {
      throw createError('ScriptWhere.model', 'Invalid modelId', {
        modelId: this.modelId,
      })
    }
    return model
  }

  @computed
  get script(): Script | null {
    return ScriptStore.get(this.scriptId)
  }

  @action
  add(
    script: Omit<IScriptWhereCreatePayload, 'id' | 'scriptId'>,
    options?: IBaseListAddOptions
  ) {
    const createdWhereItem = super.add(
      {
        ...script,
        scriptId: this.scriptId,
      },
      options
    )

    // This makes sure the Script itself knows that something changed. Will indirectly make sure that changes to where are saved back to IndexedDB
    this.script?.update({ where: [] }, options) // The actual value does not matter, because that comes from the `serialize` function below

    return createdWhereItem
  }

  serialize = () => {
    return Object.values(this.values).map(w => w.serialize())
  }
}

export default ScriptWhere
